import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { Button } from '../../../../shared/components/index.js';

interface SearchHit {
  readonly searchIndexId: string;
  readonly indexCode: string;
  readonly sourceModule: string;
  readonly contentType: string;
  readonly title: string;
  readonly snippet: string;
  readonly classificationLevel: string;
  readonly rank: number;
  readonly relevanceScore: number;
}
interface SearchOutcome {
  readonly searchQueryId: string;
  readonly queryCode: string;
  readonly query: string;
  readonly searchType: string;
  readonly status: string;
  readonly resultCount: number;
  readonly executionMs: number;
  readonly results: readonly SearchHit[];
}
interface AskCitation {
  readonly title: string;
  readonly sourceModule: string;
  readonly snippet: string;
  readonly searchIndexId: string;
}
interface AskOutcome {
  readonly searchQueryId: string;
  readonly question: string;
  readonly answer: string;
  readonly confidence: number;
  readonly citations: readonly AskCitation[];
  readonly resultCount: number;
}

const MODULES = ['DMS', 'KMS', 'DAM', 'ANALYTICS', 'CONTRACTS', 'CAMPAIGNS', 'VENDORS', 'WORKFLOWS', 'APPROVALS', 'PROJECTS'];
const CONTENT_TYPES = ['DOCUMENT', 'CAMPAIGN', 'CONTRACT', 'VENDOR_RECORD', 'ASSET', 'POLICY', 'SOP', 'KNOWLEDGE_ARTICLE', 'REPORT'];
const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
const ADVANCED_TYPES = ['ADVANCED', 'BOOLEAN', 'WILDCARD', 'FUZZY', 'PHRASE'];

/**
 * M39 — Advanced Search Screen (§7/§23). Power-user query construction across the
 * three governed engines that need SEARCH_ADVANCED (SoD, §27): field-scoped
 * advanced/boolean queries, semantic similarity search, and AI-assisted
 * natural-language "ask" that returns a composed answer with citations (§14).
 * Module / content-type / classification filters bound the search; results are
 * ACL-trimmed and ranked server-side (§13). A 403 surfaces the required
 * permission; every query is logged for audit (BR-03).
 */
export function AdvancedSearchScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();

  const { number } = useBusinessNumber();
  const [mode, setMode] = useState<'advanced' | 'semantic' | 'ask'>('advanced');
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('ADVANCED');
  const [modules, setModules] = useState<string[]>([]);
  const [contentTypes, setContentTypes] = useState<string[]>([]);
  const [classification, setClassification] = useState('');
  const [outcome, setOutcome] = useState<SearchOutcome | null>(null);
  const [ask, setAsk] = useState<AskOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = (list: string[], value: string, set: (v: string[]) => void): void => {
    set(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setOutcome(null);
    setAsk(null);
    if (!query.trim()) { setError(t('m39.advancedSearch.enterQuery')); return; }
    setBusy(true);
    const body: Record<string, unknown> = {
      query: query.trim(),
      sourceModules: modules.length ? modules : undefined,
      contentTypes: contentTypes.length ? contentTypes : undefined,
      classification: classification || undefined,
    };
    const path = mode === 'advanced' ? '/v1/search/advanced' : mode === 'semantic' ? '/v1/search/semantic' : '/v1/search/ask';
    if (mode === 'advanced') body.searchType = searchType;
    try {
      if (mode === 'ask') {
        const r = await apiRequest<{ data: AskOutcome }>(path, { method: 'POST', body, token });
        setAsk(r.data);
      } else {
        const r = await apiRequest<{ data: SearchOutcome }>(path, { method: 'POST', body, token });
        setOutcome(r.data);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(t('m39.advancedSearch.notAuthorised'));
      } else {
        setError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="advanced-search-screen">
      <h1>{t('m39.advancedSearch.title')}</h1>

      <nav aria-label="search-mode">
        <Button variant="secondary" type="button" aria-label="mode-advanced" aria-pressed={mode === 'advanced'} onClick={() => setMode('advanced')}>{t('m39.advancedSearch.modeAdvanced')}</Button>
        <Button variant="secondary" type="button" aria-label="mode-semantic" aria-pressed={mode === 'semantic'} onClick={() => setMode('semantic')}>{t('m39.advancedSearch.modeSemantic')}</Button>
        <Button variant="secondary" type="button" aria-label="mode-ask" aria-pressed={mode === 'ask'} onClick={() => setMode('ask')}>{t('m39.advancedSearch.modeAsk')}</Button>
      </nav>

      <form aria-label="advanced-form" onSubmit={(e) => void submit(e)}>
        <label>{mode === 'ask' ? t('m39.advancedSearch.question') : t('m39.advancedSearch.queryExpression')}
          <textarea aria-label="field-query" value={query} rows={3} onChange={(e) => setQuery(e.target.value)}
            placeholder={mode === 'advanced' ? 'e.g. northern AND (hub OR depot) NOT draft' : mode === 'ask' ? 'e.g. what is our northern fulfilment level?' : t('m39.advancedSearch.describePlaceholder')} />
        </label>

        {mode === 'advanced' && (
          <label>{t('m39.advancedSearch.searchType')}
            <select aria-label="field-search-type" value={searchType} onChange={(e) => setSearchType(e.target.value)}>
              {ADVANCED_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
        )}

        <fieldset aria-label="field-modules">
          <legend>{t('m39.advancedSearch.modulesLegend')}</legend>
          {MODULES.map((m) => (
            <label key={m}>
              <input type="checkbox" aria-label={`module-${m}`} checked={modules.includes(m)} onChange={() => toggle(modules, m, setModules)} />{m}
            </label>
          ))}
        </fieldset>

        <fieldset aria-label="field-content-types">
          <legend>{t('m39.advancedSearch.contentTypesLegend')}</legend>
          {CONTENT_TYPES.map((c) => (
            <label key={c}>
              <input type="checkbox" aria-label={`content-${c}`} checked={contentTypes.includes(c)} onChange={() => toggle(contentTypes, c, setContentTypes)} />{c}
            </label>
          ))}
        </fieldset>

        <label>{t('m39.common.classification')}
          <select aria-label="field-classification" value={classification} onChange={(e) => setClassification(e.target.value)}>
            <option value="">{t('m39.advancedSearch.anyAuthorised')}</option>
            {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        <Button variant="primary" type="submit" aria-label="submit-advanced" disabled={busy}>{busy ? t('m39.advancedSearch.running') : t('m39.advancedSearch.runSearch')}</Button>
      </form>

      {error && <p role="alert" aria-label="advanced-error">{error}</p>}

      {ask && (
        <section aria-label="ask-answer">
          <h2>{t('m39.advancedSearch.answer')}</h2>
          <p aria-label="answer-text">{ask.answer}</p>
          <p aria-label="answer-confidence">{t('m39.advancedSearch.confidence', { pct: number(ask.confidence * 100, 0) })}</p>
          <h3>{t('m39.advancedSearch.citations')}</h3>
          {ask.citations.length === 0 ? (
            <p aria-label="citations-empty">{t('m39.advancedSearch.citationsEmpty')}</p>
          ) : (
            <ul aria-label="citations-list">
              {ask.citations.map((c) => (
                <li key={c.searchIndexId} data-citation={c.searchIndexId}>
                  <strong>{c.title}</strong> · {c.sourceModule}
                  <p>{c.snippet}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {outcome && (
        <section aria-label="advanced-results">
          <p aria-label="advanced-summary">{t('m39.advancedSearch.summary', { count: outcome.resultCount, searchType: outcome.searchType, ms: outcome.executionMs })}</p>
          {outcome.results.length === 0 ? (
            <p aria-label="advanced-empty">{t('m39.advancedSearch.empty')}</p>
          ) : (
            <ol aria-label="advanced-results-list">
              {outcome.results.map((hit) => (
                <li key={hit.searchIndexId} data-result={hit.indexCode}>
                  <strong>{hit.title}</strong>
                  <span aria-label={`adv-module-${hit.indexCode}`}>{hit.sourceModule}</span>
                  <span aria-label={`adv-score-${hit.indexCode}`}>{number(hit.relevanceScore, 3)}</span>
                  <p>{hit.snippet}</p>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
    </main>
  );
}
